/**
 * almanacModel — pure logic behind the Traveler's Almanac (C5 Layer 3).
 *
 * The Almanac is a two-page paper spread that opens off the day-wheel:
 *   left page  — one hanko stamp per day lived, plus the moon as an ink diagram
 *   right page — today's marks (journal/festival entries, whispers) and a few
 *                small ink annotations from the lifetime stats.
 *
 * Everything here is Phaser-free, DOM-free and locale-free: the module returns
 * shapes and semantic names, the view layer turns them into elements and
 * translated strings. Tested in the Node vitest env like the other c5/* modules.
 */

// ── the days (left page) ───────────────────────────────────────────────────

/** Stamps per row on the page — a week reads naturally as a row of seven. */
export const STAMPS_PER_ROW = 7;

/** Most stamps the page holds before it starts summarising older days. */
export const MAX_STAMPS = 84; // 12 rows of 7

export interface StampCell {
  day: number;
  /** True for the day currently being lived — drawn wet, settling on open. */
  fresh: boolean;
}

export interface StampGrid {
  cells: StampCell[];
  columns: number;
  rows: number;
  /** Days that fell off the top of the page. 0 when everything fits. */
  earlierDays: number;
}

/**
 * Lay out one stamp per day lived, oldest first, newest last.
 *
 * Past `maxStamps` the page shows the most recent window and reports how many
 * days came before it — a long-lived traveler gets a summary line, not a page
 * that grows without bound.
 */
export function buildStampGrid(
  dayCount: number,
  maxStamps: number = MAX_STAMPS,
  perRow: number = STAMPS_PER_ROW,
): StampGrid {
  const columns = Math.max(1, Math.floor(perRow));
  const cap = Math.max(1, Math.floor(maxStamps));
  const days = Number.isFinite(dayCount) ? Math.max(1, Math.floor(dayCount)) : 1;
  const visible = Math.min(days, cap);
  const firstDay = days - visible + 1;

  const cells: StampCell[] = [];
  for (let day = firstDay; day <= days; day++) {
    cells.push({ day, fresh: day === days });
  }

  return {
    cells,
    columns,
    rows: Math.ceil(visible / columns),
    earlierDays: days - visible,
  };
}

// ── the moon (left page) ───────────────────────────────────────────────────

export type MoonName =
  | 'new'
  | 'waxing-crescent'
  | 'first-quarter'
  | 'waxing-gibbous'
  | 'full'
  | 'waning-gibbous'
  | 'last-quarter'
  | 'waning-crescent';

const MOON_NAMES: readonly MoonName[] = [
  'new',
  'waxing-crescent',
  'first-quarter',
  'waxing-gibbous',
  'full',
  'waning-gibbous',
  'last-quarter',
  'waning-crescent',
];

export interface MoonDiagram {
  name: MoonName;
  /** Lit fraction of the disc, 0 (new) .. 1 (full). */
  illumination: number;
  /** True while the moon is filling (phase < 0.5). */
  waxing: boolean;
  /**
   * Width of the terminator ellipse as a fraction of the disc diameter —
   * 1 at new/full (the terminator is the disc outline seen edge-on… well,
   * a full circle), 0 at the quarters (a straight line).
   */
  terminatorRatio: number;
  /**
   * Whether the terminator ellipse should be filled with ink (crescent
   * moons: the ellipse carves darkness out of the lit half) or with paper
   * (gibbous moons: the ellipse spills light into the dark half).
   */
  terminatorInk: boolean;
}

function wrapPhase(phase: number): number {
  if (!Number.isFinite(phase)) return 0;
  return ((phase % 1) + 1) % 1;
}

/**
 * Everything the view needs to draw the moon in ink, from a lunar phase in
 * [0,1) where 0 = new and 0.5 = full (the `@game/shared` convention).
 */
export function moonDiagram(phase: number): MoonDiagram {
  const p = wrapPhase(phase);
  const illumination = (1 - Math.cos(2 * Math.PI * p)) / 2;
  return {
    name: MOON_NAMES[Math.round(p * 8) % 8],
    illumination,
    waxing: p < 0.5,
    terminatorRatio: Math.abs(Math.cos(2 * Math.PI * p)),
    terminatorInk: illumination < 0.5,
  };
}

/** Locale key for a moon name, e.g. `c5.almanac.moon.waxing_crescent`. */
export function moonLocaleKey(name: MoonName): string {
  return `c5.almanac.moon.${name.replace(/-/g, '_')}`;
}

// ── today's marks (right page) ─────────────────────────────────────────────

/** How many pinned slips the right page holds. */
export const MAX_MARKS = 6;

/** Characters of body text a pinned slip shows before it trails off. */
export const MARK_BODY_LIMIT = 150;

export interface MarkSource {
  id: string;
  heading: string;
  body: string;
  timestamp: number;
}

export interface AlmanacMark {
  id: string;
  heading: string;
  body: string;
  timestamp: number;
}

/** Collapse whitespace and trail off past `limit` characters. */
export function truncateBody(body: string, limit: number = MARK_BODY_LIMIT): string {
  const text = body.replace(/\s+/g, ' ').trim();
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * Newest-first selection of marks for the right page.
 *
 * Drops entries with nothing to say, de-duplicates by id (keeping the newest
 * occurrence), and caps the page. Input order is not trusted — festival
 * entries arrive appended, whispers arrive out of band.
 */
export function selectMarks(
  sources: readonly MarkSource[],
  limit: number = MAX_MARKS,
  bodyLimit: number = MARK_BODY_LIMIT,
): AlmanacMark[] {
  const cap = Math.max(0, Math.floor(limit));
  if (cap === 0) return [];

  const byId = new Map<string, MarkSource>();
  for (const src of sources) {
    if (!src) continue;
    const heading = (src.heading ?? '').trim();
    const body = (src.body ?? '').trim();
    if (heading.length === 0 && body.length === 0) continue;
    const at = Number.isFinite(src.timestamp) ? src.timestamp : 0;
    const prev = byId.get(src.id);
    if (prev && prev.timestamp >= at) continue;
    byId.set(src.id, { id: src.id, heading, body, timestamp: at });
  }

  return [...byId.values()]
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, cap)
    .map((m) => ({ ...m, body: truncateBody(m.body, bodyLimit) }));
}

// ── ink annotations (right page footer) ────────────────────────────────────

export interface AlmanacStatsInput {
  runs: number;
  unburdened: number;
  gathered: number;
  deepestFloor: number;
  /** Pre-formatted playtime label (e.g. "3h 20m"); empty when unknown. */
  playtimeLabel: string;
}

export interface AlmanacAnnotation {
  /** Locale key for the label, e.g. `c5.almanac.stat.runs`. */
  labelKey: string;
  value: string;
}

/**
 * Small marginal notes from the lifetime stats. Only facts the traveler has
 * actually accumulated appear — an almanac of zeroes is noise, and paper is
 * always earned (doctrine, "Paper & ink").
 */
export function buildAnnotations(stats: AlmanacStatsInput): AlmanacAnnotation[] {
  const out: AlmanacAnnotation[] = [];
  const push = (labelKey: string, n: number): void => {
    if (Number.isFinite(n) && n > 0) out.push({ labelKey, value: String(Math.floor(n)) });
  };
  push('c5.almanac.stat.runs', stats.runs);
  push('c5.almanac.stat.unburdened', stats.unburdened);
  push('c5.almanac.stat.gathered', stats.gathered);
  push('c5.almanac.stat.depth', stats.deepestFloor);
  const playtime = (stats.playtimeLabel ?? '').trim();
  if (playtime.length > 0 && playtime !== '0m') {
    out.push({ labelKey: 'c5.almanac.stat.walked', value: playtime });
  }
  return out;
}
